// hooks/UserData.js
import { useEffect, useState, useContext } from "react";
import { useAuthToken } from "./UseAuthToken";
import { UserContext } from "../context/UserContext";
import axios from "axios";

const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

const useUserData = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = useAuthToken();

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      // If we have no token yet, we're still waiting → stay loading
      if (!token) return;

      if (alive) setIsLoading(true);

      try {
        const response = await axios.get(`${SERVER_BASE_URL}/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setUser(response.data);
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      alive = false;
    };
  }, [token]);

  return [user, isLoading, error];
};

const useBasicUserData = () => {
  const { basicUserData, setBasicUserData } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useAuthToken();

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      // 1) No token yet → auth still resolving, keep loading
      if (!token) return;

      // 2) Already have basicUserData in context → just mark done
      if (basicUserData) {
        if (alive) setIsLoading(false);
        return;
      }

      if (alive) setIsLoading(true);

      try {
        const response = await axios.get(`${SERVER_BASE_URL}/user/basic`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (alive) {
          setBasicUserData(response.data);
          setIsLoading(false);
        }
      } catch (e) {
        if (alive) {
          setError(e);
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      alive = false;
    };
  }, [token, basicUserData, setBasicUserData]);

  return [basicUserData, isLoading, error];
};

const useDeleteUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = useAuthToken();

  const deleteUser = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      await axios.delete(`${SERVER_BASE_URL}/user/delete`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteUser, isLoading, error };
};

export { useUserData, useBasicUserData, useDeleteUser };
